import { useCallback } from "react";
import confirm from "@app/services/confirm/confirm";
import notification from "@app/services/notification";
import { storePlacesServices, type StorePlaceCancelSdbPlacementParams } from "@src/businessModules/Stock/api/storePlaces";
import {
    buildStockFinishedGoodsReceiptCancelConfirmOptions,
    type BuildStockFinishedGoodsReceiptCancelConfirmParams,
} from "./buildStockFinishedGoodsReceiptCancelConfirmOptions";
import type { FinishedGoodsReceiptCancelPlacementBySdbVariables } from "./useStockFinishedGoodsReceiptCardMutations";

/** Параметры подтверждения отмены размещения продукции. */
export interface ConfirmStockFinishedGoodsReceiptCancelParams
    extends Required<Pick<StorePlaceCancelSdbPlacementParams, "sdbCode" | "cellCode">> {
    /** Код строки состава ВЗН УП для точечного refetch после отмены. */
    contentCode: number;
    /** Поля контекста строки ВЗН УП для confirm. */
    fields: BuildStockFinishedGoodsReceiptCancelConfirmParams["fields"];
}

/** Параметры хука {@link useStockFinishedGoodsReceiptCancelConfirm}. */
export interface UseStockFinishedGoodsReceiptCancelConfirmParams {
    /** Действие отмены размещения после подтверждения (обычно `mutation.mutateAsync`). */
    perform: (variables: FinishedGoodsReceiptCancelPlacementBySdbVariables) => Promise<unknown>;
}

/**
 * Подтверждение и запуск отмены размещения продукции через app confirm.
 * Сначала загружает движение для preview, затем показывает confirm с DetailList.
 */
export function useStockFinishedGoodsReceiptCancelConfirm({ perform }: UseStockFinishedGoodsReceiptCancelConfirmParams) {
    const confirmAndCancel = useCallback(
        async (params: ConfirmStockFinishedGoodsReceiptCancelParams): Promise<void> => {
            const movement = await storePlacesServices.loadLatestSdbPlacementMovement({
                sdbCode: params.sdbCode,
                cellCode: params.cellCode,
            });

            if (movement == null) {
                notification.warning("Движение размещения не найдено для отмены.");
                return;
            }

            const accepted = await confirm.show(
                buildStockFinishedGoodsReceiptCancelConfirmOptions({
                    movement,
                    fields: params.fields,
                }),
            );

            if (!accepted) {
                return;
            }

            await perform({
                sdbCode: params.sdbCode,
                cellCode: params.cellCode,
                contentCode: params.contentCode,
            });
        },
        [perform],
    );

    return { confirmAndCancel };
}
